import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router';

import heroBanner from '@/assets/hero/pets-e-racoes.jpeg';
import { SITE } from '@/config/site';
import { PET_TYPES } from '@/lib/taxonomy';
import { WA_LINKS } from '@/lib/whatsapp';

import { Container } from '../common/Container';
import { PawIcon, WhatsAppIcon } from '../common/icons';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

/**
 * First fold.
 *
 * The client's marked-up feedback flagged the wordmark repeated below the
 * logo as noise, so the headline carries the page on its own and the promise
 * gets the space the duplicate title used to take.
 */
export function Hero() {
  return (
    <section className="relative overflow-hidden pt-10 pb-0 lg:pt-16">
      {/* Decorative paws — hidden from assistive tech. */}
      <PawIcon
        className="pointer-events-none absolute top-12 left-[6%] hidden size-24 animate-float-paw text-caramel-500/10 lg:block"
        aria-hidden="true"
      />
      <PawIcon
        className="pointer-events-none absolute top-40 right-[8%] hidden size-16 animate-float-paw text-caramel-500/10 lg:block"
        style={{ animationDelay: '1.4s' }}
        aria-hidden="true"
      />

      <Container className="flex flex-col items-center gap-6 text-center">
        <Badge variant="soft" size="lg" className="max-w-full animate-pop-in whitespace-normal">
          <PawIcon className="size-4" />
          Distribuidora de rações em {SITE.serviceArea}
        </Badge>

        <h1 className="max-w-4xl text-[clamp(2.5rem,7vw,4.5rem)] leading-[1.05] font-extrabold text-brown-900">
          Rações de confiança
          <br />
          para o seu <span className="text-orange-500">pet</span>
        </h1>

        <p className="max-w-2xl text-base leading-relaxed text-brown-700 lg:text-lg">
          Trabalhamos com as marcas líderes em nutrição para cães e gatos. Monte seu pedido pelo
          site e finalize no WhatsApp com atendimento humano e entrega rápida.
        </p>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <Button asChild size="lg" className="sm:min-w-56">
            <Link to="/catalogo">
              Ver catálogo
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg">
            <a href={WA_LINKS.general} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon aria-hidden="true" />
              Falar no WhatsApp
            </a>
          </Button>
        </div>

        {/* Fast lane straight into the two halves of the catalogue. */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span className="text-sm font-semibold text-brown-700">Comprar para:</span>

          {(['dog', 'cat'] as const).map((pet) => (
            <Link
              key={pet}
              to={`/catalogo?pet=${pet}`}
              className="inline-flex items-center gap-2 rounded-full border-2 border-sand-300 bg-white px-4 py-2 font-display text-sm font-bold text-brown-800 transition-colors hover:border-caramel-500 hover:bg-cream-200"
            >
              <span aria-hidden="true">{PET_TYPES[pet].emoji}</span>
              {PET_TYPES[pet].label}
            </Link>
          ))}

          <Link
            to="/catalogo?line=super-premium"
            className="inline-flex items-center gap-2 rounded-full border-2 border-sand-300 bg-white px-4 py-2 font-display text-sm font-bold text-brown-800 transition-colors hover:border-caramel-500 hover:bg-cream-200"
          >
            <Sparkles className="size-4 text-orange-500" aria-hidden="true" />
            Super premium
          </Link>
        </div>
      </Container>

      {/* Client-supplied lifestyle banner, framed rather than full-bleed. */}
      <Container className="mt-10 lg:mt-14">
        <div className="relative overflow-hidden rounded-3xl border-2 border-sand-300 shadow-card">
          <img
            src={heroBanner}
            alt="Cães e gatos ao lado de embalagens de ração PremierPet, Golden e Royal Canin"
            width={1600}
            height={430}
            fetchPriority="high"
            className="aspect-[5/2] w-full object-cover object-center sm:aspect-auto"
          />

          <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full border border-sand-300 bg-white/90 px-3 py-1.5 backdrop-blur-sm lg:bottom-5 lg:left-5">
            <PawIcon className="size-3.5 text-caramel-500" />
            <span className="font-display text-[11px] font-bold text-brown-900 lg:text-xs">
              Mais de 20 marcas para cães e gatos
            </span>
          </div>
        </div>
      </Container>
    </section>
  );
}
